import React, { useState, useEffect, useRef } from "react";
import { FaEye, FaEdit, FaTrash, FaSearch, FaPlus, FaTimes } from "react-icons/fa";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import useAxios from "../../api/useAxios";
import Swal from 'sweetalert2';


const Sizes = () => {
  const axiosInstance = useAxios();
  const [sizes, setSizes] = useState([
  { id: 1, name: "Litres", shortForm: "lt"},
  { id: 2, name: "Milli Litres", shortForm: "ml" },
  { id: 3, name: "Kilo Grams", shortForm: "kg" },
  ]);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editSizeId, setEditSizeId] = useState(null);
  const [newSize, setNewSize] = useState({
    name: "",
    shortForm: "",
    
  });
  const [viewSize, setViewSize] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const fileInputRef = useRef(null);
  const [statusFilter, setStatusFilter] = useState("all");

  // Fetch Sizes
  const fetchSizes = async () => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.get("/sizes");
      setSizes(res.data.data);
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch sizes',
        timer: 3000,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSizes();
  }, []);

  // Sorting function
  const sortedSizes = [...sizes].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Filter & Pagination
  const filteredSizes = sortedSizes.filter((b) => {
    const searchMatch = b.name.toLowerCase().includes(search.toLowerCase()) ||
      (b.shortForm && b.shortForm.toLowerCase().includes(search.toLowerCase()));

    const statusMatch = statusFilter === "all" ||
      (statusFilter === "active" && b.active) ||
      (statusFilter === "inactive" && !b.active);

    return searchMatch && statusMatch;
  });

  const totalEntries = filteredSizes.length;
  const totalPages = Math.ceil(totalEntries / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalEntries);
  const currentSizes = filteredSizes.slice(startIndex, endIndex);

  // Handle Sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Handle Save (Create/Update)
  const handleSaveSize = async () => {
    if (!newSize.name.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Size name is required',
        timer: 3000,
        showConfirmButton: false,
      });
      return;
    }

    const sizeData = {
      name: newSize.name.trim(),
      shortForm: newSize.shortForm || "",
    };

    setIsLoading(true);
    try {
      if (editSizeId) {
        await axiosInstance.put(`/sizes/${editSizeId}`, sizeData);
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Size updated successfully!',
          timer: 3000,
          showConfirmButton: false,
        });
      } else {
        await axiosInstance.post("/sizes", sizeData);
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Size created successfully!',
          timer: 3000,
          showConfirmButton: false,
        });
      }

      setShowModal(false);
      setEditSizeId(null);
      setNewSize({
        name: "",
        shortform: "",
      });
      fetchSizes();
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || 'Error saving size. Please try again.',
        timer: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Delete with SweetAlert
  const handleDeleteSize = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`/sizes/${id}`);

        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Size has been deleted.',
          timer: 3000,
          showConfirmButton: false,
        });

        fetchSizes();
      } catch (err) {
        console.error(err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.response?.data?.message || 'Error deleting size. Please try again.',
          timer: 3000,
        });
      }
    }
  };


  // Reset modal
  const resetModal = () => {
    setNewSize({
      name: "",
      shortForm:"",
    });
    setEditSizeId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Open edit modal with size data
  const openEditModal = (size) => {
    setNewSize({
      name: size.name,
      shortForm: size.shortForm || "",
    });
    setEditSizeId(size.id);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setShowModal(true);
  };

 

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      {/* HEADER */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Size Management</h1>
        <p className="text-gray-600">Manage your size details, status.</p>
      </div>

      {/* CONTROLS CARD */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex flex-col md:flex-row gap-4 flex-1">
            {/* SEARCH */}
            <div className="relative flex-1 max-w-md">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search sizes by name or short form..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <FaTimes />
                </button>
              )}
            </div>

            {/* STATUS FILTER */}
            <div className="flex items-center gap-3">
              <div className="relative">

                <IoMdArrowDropdown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
              </div>
            </div>

            {/* ROWS PER PAGE */}
            <div className="flex items-center gap-3">
              <span className="text-gray-600 whitespace-nowrap">Show</span>
              <div className="relative">
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (value > 0) {
                      setRowsPerPage(value);
                      setCurrentPage(1);
                    }
                  }}
                  className="appearance-none px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white pr-10"
                >
                  {[5, 10, 20, 50].map((num) => (
                    <option key={num} value={num}>
                      {num} rows
                    </option>
                  ))}
                </select>
                <IoMdArrowDropdown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* ADD BUTTON */}
          <button
            onClick={() => {
              resetModal();
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg whitespace-nowrap"
          >
            <FaPlus />
            <span>Add New Size</span>
          </button>
        </div>
      </div>


      {/* MAIN TABLE CARD */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
        {/* TABLE HEADER */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <h2 className="text-lg font-semibold text-gray-800">Size Details</h2>
          <div className="text-sm text-gray-600 mt-1">
            {statusFilter === "all" ? "Showing all brands" :
              statusFilter === "active" ? "Showing active brands only" :
                "Showing inactive brands only"}
          </div>
        </div>

        {/* TABLE CONTAINER */}
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="py-20 flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 md:px-6 py-4 text-left font-semibold text-gray-700">#</th>
                  <th
                    className="px-6 py-4 text-left font-semibold text-gray-700 cursor-pointer"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-2">
                      Size Type
                      {sortConfig.key === 'name' && (
                        sortConfig.direction === 'asc' ? <IoMdArrowDropup /> : <IoMdArrowDropdown />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left font-semibold text-gray-700">Short Form</th>
                  <th className="px-6 py-4 text-center font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentSizes.map((size, index) => (
                  <tr
                    key={size.id}
                    className={`border-b border-gray-100 hover:bg-blue-50 transition-colors ${!size.active ? "bg-blur-50 hover:bg-blue-100" : ""
                      }`}
                  >
                    {/* sno */}
                    <td className="px-4 md:px-6 py-4">
                        <div className="text-gray-600 font-medium">{startIndex + index + 1}</div>
                      </td>
                    {/* Size Name */}
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">{size.name}</div>
                      </div>
                    </td>

                    {/* Short Form */}
                    <td className="px-6 py-4">
                      <div className="text-gray-700">{size.shortForm || "-"}</div>
                    </td>

                     {/* Actions */}
                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => {
                            setViewSize(size);
                            setShowViewModal(true);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <FaEye size={18} />
                        </button>
                        <button
                          onClick={() => openEditModal(size)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Edit Size"
                        >
                          <FaEdit size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteSize(size.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Size"
                        >
                          <FaTrash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* EMPTY STATE */}
          {!isLoading && currentSizes.length === 0 && (
            <div className="py-16 text-center">
              <div className="text-gray-400 mb-4">
                <FaSearch size={48} className="mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-600 mb-2">
                No sizes found
              </h3>
              <p className="text-gray-500">
                {search || statusFilter !== "all"
                  ? "Try adjusting your search or filter terms"
                  : "Add your first size to get started"}
              </p>
            </div>
          )}
        </div>

        {/* PAGINATION */}
        {totalEntries > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-600">
                Showing <span className="font-semibold">{startIndex + 1}</span> to{" "}
                <span className="font-semibold">{endIndex}</span> of{" "}
                <span className="font-semibold">{totalEntries}</span> sizes
              </div>

              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className={`px-4 py-2 rounded-lg border transition-colors ${currentPage === 1
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                      }`}
                  >
                    Previous
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={i}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-1 rounded transition-colors ${currentPage === pageNum
                              ? "bg-blue-600 text-white"
                              : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300"
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-2 rounded-lg border transition-colors ${currentPage === totalPages
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                      }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ADD/EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md animate-scale-in">
            {/* MODAL HEADER */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-800">
                  {editSizeId ? "Edit Size" : "Add New Size"}
                </h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetModal();
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>

            {/* MODAL BODY */}
            <div className="p-6 space-y-4">
              {/* Size Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Size Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter size Name"
                  value={newSize.name}
                  onChange={(e) => setNewSize({ ...newSize, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
              </div>
              {/* Short Form */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Short Form <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter short form"
                  value={newSize.shortForm || ""}
                  onChange={(e) => setNewSize({ ...newSize, shortForm: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
              </div>
            </div>

            {/* MODAL FOOTER */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetModal();
                  }}
                  className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSize}
                  disabled={isLoading || !newSize.name.trim()}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : editSizeId ? (
                    "Update Size"
                  ) : (
                    "Create Size"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* VIEW MODAL */}
      {showViewModal && viewSize && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md animate-scale-in">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-800">Size Details</h3>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">ID</label>
                    <p className="text-gray-900 font-medium">{viewSize.id}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Name</label>
                    <p className="text-gray-900 font-medium">{viewSize.name}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Short Form</label>
                    <p className="text-gray-900 font-medium">{viewSize.shortForm || "-"}</p>
                  </div>
                </div>


              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sizes;